import { fetcher } from '@commenda/utils';

/**
 * A single filing diff entry: shows old vs new due dates side by side.
 * Identified by its composite key (corporationId, complianceType, effectiveStartDate, effectiveEndDate).
 */
export interface FilingDiff {
  corporationId: number;
  complianceType: string;
  displayName: string;
  effectiveYear: number;
  effectiveStartDate: string;
  effectiveEndDate: string;

  /** Current due date from the existing filing. Null for brand new filings. */
  previousDueDate: string | null;
  /** Current status of the existing filing. Null for brand new filings. */
  previousStatus: string | null;

  /** The due date that would be set after regeneration. */
  newDueDate: string;

  /** True if this filing does not currently exist in the database. */
  isNew: boolean;
  /** True if the due date has changed (or the filing is new). */
  hasDueDateChange: boolean;
}

export interface CorporationFilingPreview {
  corporationId: number;
  corporationName: string;
  filingDiffs: FilingDiff[];
  error?: string;
}

export interface AdminBulkRegenerateFilingsPreviewResponse {
  previews: CorporationFilingPreview[];
  summary: {
    totalCorporations: number;
    totalFilingsWithChanges: number;
    totalNewFilings: number;
  };
}

export interface FilingToRegenerate {
  corporationId: number;
  complianceType: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
}

export interface AdminBulkRegenerateFilingsResult {
  corporationId: number;
  corporationName: string;
  success: boolean;
  error?: string;
  filingsCreated: number;
  filingsDeleted: number;
  serviceRequestsCreated: number;
}

export interface AdminBulkRegenerateFilingsResponse {
  results: AdminBulkRegenerateFilingsResult[];
  summary: {
    totalCorporations: number;
    successCount: number;
    failureCount: number;
    totalFilingsCreated: number;
    totalFilingsDeleted: number;
    totalServiceRequestsCreated: number;
  };
}

interface PreviewParams {
  /** Ignored when allEntitlementCustomers is true. */
  corporationIds?: number[];
  complianceTypes: string[];
  fiscalYear: number;
  forceRegenerate?: boolean;
  /** When true, the backend resolves the corporation set to all entitlement customers and ignores corporationIds. */
  allEntitlementCustomers?: boolean;
  token?: string;
}

interface RegenerateParams {
  fiscalYear: number;
  filingsToRegenerate: FilingToRegenerate[];
  forceRegenerate?: boolean;
  attachWorkflow?: boolean;
  token?: string;
}

/**
 * Preview the filing diff before regeneration.
 * Returns each filing with its previous due date and new due date side by side.
 * This does NOT make any changes.
 */
export async function adminPreviewBulkRegenerateFilings({
  corporationIds,
  complianceTypes,
  fiscalYear,
  forceRegenerate,
  allEntitlementCustomers,
  token,
}: PreviewParams): Promise<AdminBulkRegenerateFilingsPreviewResponse> {
  const res = await fetcher(
    'compliance/admin/bulk-regenerate/preview',
    {
      method: 'POST',
      body: {
        corporationIds,
        complianceTypes,
        fiscalYear,
        forceRegenerate,
        allEntitlementCustomers,
      },
    },
    token
  );

  return res.json();
}

/**
 * Regenerate specific filings selected from the preview diff.
 * Each filing is identified by its composite key (corporationId, complianceType, effectiveStartDate, effectiveEndDate).
 */
export async function adminBulkRegenerateFilings({
  fiscalYear,
  filingsToRegenerate,
  forceRegenerate,
  attachWorkflow,
  token,
}: RegenerateParams): Promise<AdminBulkRegenerateFilingsResponse> {
  const res = await fetcher(
    'compliance/admin/bulk-regenerate',
    {
      method: 'POST',
      body: {
        fiscalYear,
        filingsToRegenerate,
        forceRegenerate,
        attachWorkflow,
      },
    },
    token
  );

  return res.json();
}

export async function getAllFilingTypes(
  token?: string
): Promise<{ type: string; title: string }[]> {
  const res = await fetcher(
    'compliance/filings',
    {
      method: 'GET',
    },
    token
  );

  return res.json();
}
